import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from './config';
import './ChatbotPage.css';
import { useAuth } from '../App';
import trashIcon from '../assets/img/trash-icon.svg';
import expandArrow from '../assets/img/expand arrow.png';
import ChatbotSendIcon from './ChatbotSendIcon';

const ChatbotPage = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, userRole, isLoading: authLoading, toggleChat } = useAuth();
  const [initialized, setInitialized] = useState(false);

  // Проверка роли пользователя при загрузке страницы
  useEffect(() => {
    // Если авторизация загружена и пользователь не имеет доступа, редиректим на главную
    if (!authLoading && (!isAuthenticated || userRole <= 1)) {
      navigate('/');
    } else if (!authLoading) {
      setIsCheckingAuth(false);
    }
  }, [isAuthenticated, userRole, authLoading, navigate]);

  useEffect(() => {
    // Проверка авторизации и роли пользователя
    if (!authLoading) {
      if (!isAuthenticated || userRole <= 1) {
        navigate('/');
      } else {
        setInitialized(true);
      }
    }
  }, [authLoading, isAuthenticated, userRole, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isAuthenticated && userRole > 1 && initialized) {
      loadMessages();
    }
    
    // Сохраняем текущую позицию прокрутки перед блокировкой
    const scrollY = window.scrollY;
    document.body.dataset.scrollY = scrollY;
    
    // Блокируем прокрутку для мобильных устройств
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    
    return () => {
      // Получаем сохраненную позицию прокрутки
      const scrollY = parseInt(document.body.dataset.scrollY || '0');
      
      // Восстанавливаем стандартные стили
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.left = '';
      
      // Восстанавливаем позицию прокрутки с задержкой
      setTimeout(() => {
        window.scrollTo({
          top: scrollY,
          behavior: 'instant' // Используем instant вместо auto для немедленной прокрутки
        });
        // Дополнительная проверка и восстановление прокрутки
        if (window.scrollY !== scrollY) {
          window.scrollTo(0, scrollY);
        }
        delete document.body.dataset.scrollY;
      }, 50);
    };
  }, [isAuthenticated, userRole, initialized]);

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('Токен не найден');
        return;
      }

      const response = await fetch(`${API_URL}/chatbot/message/get_messages?limit=50&offset=0`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        console.error('Ошибка загрузки сообщений:', response.status);
      }
    } catch (error) {
      console.error('Ошибка при загрузке сообщений:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Необходимо войти в систему для использования чат-бота');
      return;
    }

    // Получаем user_id из API /current
    let userId;
    try {
      const userResponse = await fetch(`${API_URL}/current`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        userId = userData.id;
      } else {
        throw new Error('Не удалось получить данные пользователя');
      }
    } catch (error) {
      console.error('Ошибка получения данных пользователя:', error);
      alert('Ошибка: не удается определить пользователя');
      return;
    }

    const userMessage = {
      content: newMessage,
      role: 'user',
      user_id: userId,
      id: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chatbot/message/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          content: newMessage,
          user_id: userId
        }),
      });

      if (response.ok) {
        const assistantMessage = await response.json();
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        console.error('Ошибка отправки сообщения:', response.status);
        alert('Ошибка при отправке сообщения');
      }
    } catch (error) {
      console.error('Ошибка при отправке сообщения:', error);
      alert('Ошибка при отправке сообщения');
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = async () => {
    if (!window.confirm('Вы уверены, что хотите очистить историю чата?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/chatbot/message/delete_messages`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        setMessages([]);
      } else {
        console.error('Ошибка очистки сообщений:', response.status);
      }
    } catch (error) {
      console.error('Ошибка при очистке сообщений:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Функция для возврата назад
  const goBack = () => {
    const isMobile = window.innerWidth <= 768;
    
    // Сохраняем текущую позицию прокрутки для возврата
    const scrollY = parseInt(document.body.dataset.scrollY || '0');
    sessionStorage.setItem('chatbot_return_position', scrollY.toString());
    
    // Восстанавливаем стандартные стили перед навигацией
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.top = '';
    document.body.style.left = '';
    
    // Возвращаемся на предыдущую страницу
    navigate('/');
    
    // Если это не мобильное устройство, открываем маленький чат
    if (!isMobile) {
      // Добавляем небольшую задержку, чтобы страница успела загрузиться
      setTimeout(() => {
        toggleChat(); // Используем toggleChat вместо openChatbot
        
        // Восстанавливаем позицию прокрутки после возвращения
        setTimeout(() => {
          const savedPosition = sessionStorage.getItem('chatbot_return_position');
          if (savedPosition) {
            window.scrollTo({
              top: parseInt(savedPosition),
              behavior: 'instant'
            });
            sessionStorage.removeItem('chatbot_return_position');
          }
        }, 100);
      }, 100);
    } else {
      // На мобильных устройствах сразу восстанавливаем позицию прокрутки
      setTimeout(() => {
        const savedPosition = sessionStorage.getItem('chatbot_return_position');
        if (savedPosition) {
          window.scrollTo({
            top: parseInt(savedPosition),
            behavior: 'instant'
          });
          sessionStorage.removeItem('chatbot_return_position');
        }
      }, 50);
    }
  };

  // Функция для отображения текста с учетом форматирования
  const formatMessage = (text) => {
    if (!text) return '';

    // Заменяем множественные переносы строк на специальные маркеры
    const textWithMarkers = text
      .replace(/\n\n\n/g, '\n<triple-break>\n')  // Тройной перенос
      .replace(/\n\n/g, '\n<double-break>\n');   // Двойной перенос
    
    // Разделяем текст по переносам строк
    const lines = textWithMarkers.split('\n');
    
    // Обрабатываем каждую строку отдельно
    return lines.map((line, lineIndex) => {
      // Проверяем наличие специальных маркеров для множественных переносов
      if (line === '<double-break>') {
        return <br key={`br-double-${lineIndex}`} />;
      }
      
      if (line === '<triple-break>') {
        return (
          <React.Fragment key={`br-triple-${lineIndex}`}>
            <br />
            <br />
          </React.Fragment>
        );
      }
      
      // Проверяем, начинается ли строка с ###
      if (line.startsWith('### ')) {
        // Это заголовок, отображаем его как h3
        return (
          <h3 className="message-heading" key={`heading-${lineIndex}`}>
            {line.substring(4)}
          </h3>
        );
      }
      
      // Регулярное выражение для поиска текста между двойными звездочками
      const boldRegex = /\*\*(.*?)\*\*/g;
      
      // Разбиваем текст на части: обычный текст и текст, который нужно сделать жирным
      const parts = [];
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(line)) !== null) {
        // Добавляем обычный текст перед совпадением
        if (match.index > lastIndex) {
          parts.push({
            type: 'text',
            content: line.slice(lastIndex, match.index)
          });
        }
        
        // Добавляем жирный текст
        parts.push({
          type: 'bold',
          content: match[1] // текст между звездочками
        });
        
        lastIndex = match.index + match[0].length;
      }
      
      // Добавляем оставшийся обычный текст после последнего совпадения
      if (lastIndex < line.length) {
        parts.push({
          type: 'text',
          content: line.slice(lastIndex)
        });
      }
      
      // Если совпадений не найдено, возвращаем исходный текст
      if (parts.length === 0) {
        return <React.Fragment key={lineIndex}>{line}</React.Fragment>;
      }
      
      // Преобразуем части в React-элементы
      const formattedLine = parts.map((part, partIndex) => {
        if (part.type === 'bold') {
          return <strong key={`${lineIndex}-${partIndex}`}>{part.content}</strong>;
        }
        return <span key={`${lineIndex}-${partIndex}`}>{part.content}</span>;
      });
      
      // Если это не последняя строка, добавляем перенос строки
      return (
        <React.Fragment key={lineIndex}>
          {formattedLine}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  // Функция для подстановки примера вопроса в поле ввода
  const insertExampleQuestion = (question) => {
    setNewMessage(question);
    // Фокусируемся на поле ввода после выбора примера
    const textarea = document.querySelector('.chatbot-page-input textarea');
    if (textarea) {
      textarea.focus();
    }
  };

  return (
    <>
      {isCheckingAuth || authLoading ? (
        // Показываем загрузку во время проверки авторизации
        <div className="chatbot-page">
          <div className="loading-auth">
            <div className="loading-spinner"></div>
            <p>Проверка доступа...</p>
          </div>
        </div>
      ) : (
        <div className="chatbot-page">
          <div className="chatbot-page-header">
            <button 
              onClick={goBack} 
              className="back-btn"
              title="Назад"
            >
              <img src={expandArrow} alt="Назад" className="back-arrow" /> Назад
            </button>
            <h1>AI Ассистент</h1>
            <button 
              onClick={clearMessages} 
              className="clear-history-btn"
              title="Очистить историю"
            >
              <img src={trashIcon} alt="Очистить" className="trash-icon" /> Очистить
            </button>
          </div>

          <div className="chatbot-page-messages">
            {messages.length === 0 ? (
              <div className="empty-chat-page">
                <div className="welcome-message">
                  <h2>👋 Добро пожаловать!</h2>
                  <p>Я ваш AI ассистент. Задайте мне вопрос о недвижимости, и я постараюсь помочь!</p>
                  <div className="example-questions">
                    <h3>Примеры вопросов:</h3>
                    <ul>
                      <li onClick={() => insertExampleQuestion("Расскажи о состоянии дома по адресу:")}>
                        Расскажи о состоянии дома по адресу:
                      </li>
                      <li onClick={() => insertExampleQuestion("Посоветуй дом для покупки квартиры")}>
                        Посоветуй дом для покупки квартиры
                      </li>
                      <li onClick={() => insertExampleQuestion("Какие дома сейчас в плохом состоянии")}>
                        Какие дома сейчас в плохом состоянии
                      </li>
                      <li onClick={() => insertExampleQuestion("Где самые не дорогие квартиры")}>
                        Где самые не дорогие квартиры
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((message, index) => (
                <div 
                  key={message.id || index} 
                  className={`message-page ${message.role === 'user' ? 'user-message-page' : 'assistant-message-page'}`}
                >
                  <div className="message-avatar">
                    {message.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="message-content-page">
                    <div className="message-text">
                      {message.role === 'assistant' 
                        ? formatMessage(message.content) 
                        : message.content}
                    </div>
                    <div className="message-time">
                      {message.role === 'user' ? 'Вы' : 'AI Ассистент'}
                    </div>
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div className="message-page assistant-message-page">
                <div className="message-avatar">🤖</div>
                <div className="message-content-page">
                  <div className="message-text loading-page">
                    <div className="typing-indicator-page">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span>AI думает...</span>
                  </div>
                  <div className="message-time">AI Ассистент</div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-page-input">
            <div className="input-container">
              <textarea
                placeholder="Введите ваше сообщение..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                onFocus={() => {
                  // При фокусе на поле ввода
                  document.querySelector('.chatbot-page')?.classList.add('keyboard-open');
                  // Прокручиваем к низу чата с задержкой для открытия клавиатуры
                  setTimeout(scrollToBottom, 300);
                }}
                onBlur={() => {
                  // При потере фокуса
                  document.querySelector('.chatbot-page')?.classList.remove('keyboard-open');
                }}
              />
              <button
                className="send-btn-page"
                onClick={sendMessage}
                disabled={!newMessage.trim() || isLoading}
              >
                <ChatbotSendIcon isLoading={false} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotPage; 